import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Users, CalendarDays, DollarSign, BarChart3, Settings,
  MapPin, Leaf, TrendingUp
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const GRADE_COLORS = { "A+": "bg-emerald-500", "A": "bg-green-500", "B": "bg-teal-500", "C": "bg-amber-500", "D": "bg-orange-500", "F": "bg-red-500" };

export default function LGUDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [reports, setReports] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dashRes, facRes] = await Promise.all([
        api.get("/lgu/dashboard"),
        api.get("/lgu/facilities"),
      ]);
      setDashboard(dashRes.data);
      setFacilities(facRes.data.facilities || []);
      // Load reports separately
      try {
        const repRes = await api.get("/lgu/reports");
        setReports(repRes.data);
      } catch {}
    } catch (err) {
      toast.error("Failed to load LGU dashboard");
    } finally { setLoading(false); }
  };

  const loadFacilityRules = async (venueId) => {
    setSelectedFacility(venueId);
    try {
      const res = await api.get(`/lgu/facilities/${venueId}/rules`);
      setRules(res.data);
    } catch {
      setRules({ max_bookings_per_day: 3, max_advance_days: 14, free_for_residents: false, subsidized_rate_pct: 0, operating_hours_start: "06:00", operating_hours_end: "22:00" });
    }
  };

  const saveRules = async () => {
    if (!selectedFacility) return;
    try {
      await api.put(`/lgu/facilities/${selectedFacility}/rules`, rules);
      toast.success("Facility rules updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  const stats = dashboard?.stats || {};

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="lgu-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl">{dashboard?.provider?.name || "LGU Portal"}</h1>
          <p className="text-muted-foreground text-sm flex items-center gap-1"><MapPin className="w-3 h-3" />{dashboard?.provider?.city || "Metro Manila"} &middot; Public Facilities</p>
        </div>
        <Badge className="bg-green-600 text-white">LGU</Badge>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="facilities">Facilities</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 text-center">
              <Building2 className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{stats.total_venues || 0}</p>
              <p className="text-xs text-muted-foreground">Facilities</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <CalendarDays className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{stats.total_bookings || 0}</p>
              <p className="text-xs text-muted-foreground">Total Bookings</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <Users className="w-5 h-5 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{stats.unique_players || 0}</p>
              <p className="text-xs text-muted-foreground">Unique Players</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">₱{(stats.total_revenue || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Revenue</p>
            </CardContent></Card>
          </div>

          {/* Venue list with ESG */}
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Your Facilities</CardTitle></CardHeader>
            <CardContent>
              {(dashboard?.venues || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-4 text-sm">No facilities registered yet</p>
              ) : (
                <div className="space-y-2">
                  {(dashboard?.venues || []).map(v => (
                    <div key={v.venue_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="text-sm font-medium">{v.name}</p>
                        <div className="flex gap-1 mt-0.5">
                          {(v.sport_types || []).map(s => <Badge key={s} variant="outline" className="text-[9px] capitalize">{s}</Badge>)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {v.esg_grade && (
                          <Badge className={`${GRADE_COLORS[v.esg_grade] || "bg-gray-500"} text-white text-xs`}>
                            <Leaf className="w-3 h-3 mr-1" />ESG {v.esg_grade}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent bookings */}
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">Recent Bookings</CardTitle></CardHeader>
            <CardContent>
              {(dashboard?.recent_bookings || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-4 text-sm">No bookings yet</p>
              ) : (
                <div className="space-y-2">
                  {(dashboard?.recent_bookings || []).slice(0, 5).map(b => (
                    <div key={b.booking_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <div>
                        <p className="text-sm">{b.user_name} - {b.resource_name}</p>
                        <p className="text-xs text-muted-foreground">{b.start_dt?.slice(0, 10)} {b.start_dt?.slice(11, 16)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">₱{b.total_amount}</span>
                        <Badge variant={b.status === "CONFIRMED" || b.status === "COMPLETED" ? "default" : "secondary"} className="text-[10px]">{b.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Facilities */}
        <TabsContent value="facilities" className="space-y-4 mt-4">
          {facilities.map(v => (
            <Card key={v.venue_id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-lg font-bold">{v.name}</h3>
                    <p className="text-sm text-muted-foreground">{v.address}</p>
                    <div className="flex gap-1 mt-1">
                      {(v.sport_types || []).map(s => <Badge key={s} variant="outline" className="text-[9px] capitalize">{s}</Badge>)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{v.resources?.length || 0} courts &middot; {v.booking_count || 0} bookings</p>
                  </div>
                  {v.esg_grade && (
                    <Badge className={`${GRADE_COLORS[v.esg_grade] || "bg-gray-500"} text-white`}>ESG {v.esg_grade} ({v.esg_score}%)</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Reports */}
        <TabsContent value="reports" className="space-y-4 mt-4">
          {reports?.daily_usage && (
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Daily Usage (14d)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={reports.daily_usage}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="bookings" stroke="#059669" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Sport Breakdown</CardTitle></CardHeader>
              <CardContent>
                {(reports?.sport_breakdown || []).length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">No data yet</p>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={reports.sport_breakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="sport" type="category" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#059669" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-base">Top Players</CardTitle></CardHeader>
              <CardContent>
                {(reports?.top_players || []).length === 0 ? (
                  <p className="text-muted-foreground text-center py-4 text-sm">No data yet</p>
                ) : (
                  <div className="space-y-2">
                    {reports.top_players.map((p, i) => (
                      <div key={p.user_id} className="flex items-center justify-between py-1 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-muted-foreground">#{i + 1}</span>
                          <span className="text-sm">{p.name}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{p.bookings} bookings</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Rules */}
        <TabsContent value="rules" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base flex items-center gap-2"><Settings className="w-4 h-4" /> Facility Rules</CardTitle>
              <Select value={selectedFacility || ""} onValueChange={loadFacilityRules}>
                <SelectTrigger data-testid="lgu-facility-select"><SelectValue placeholder="Select facility" /></SelectTrigger>
                <SelectContent>
                  {facilities.map(v => <SelectItem key={v.venue_id} value={v.venue_id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardHeader>
            {selectedFacility && (
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b">
                  <div><p className="text-sm font-medium">Max Bookings Per Day</p><p className="text-xs text-muted-foreground">Per user daily limit</p></div>
                  <Input type="number" className="w-20" value={rules.max_bookings_per_day || 3}
                    onChange={e => setRules({...rules, max_bookings_per_day: parseInt(e.target.value) || 3})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <div><p className="text-sm font-medium">Advance Booking Days</p><p className="text-xs text-muted-foreground">How far ahead users can book</p></div>
                  <Input type="number" className="w-20" value={rules.max_advance_days || 14}
                    onChange={e => setRules({...rules, max_advance_days: parseInt(e.target.value) || 14})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <div><p className="text-sm font-medium">Free for Residents</p><p className="text-xs text-muted-foreground">Waive booking fees for local residents</p></div>
                  <Switch checked={rules.free_for_residents || false} onCheckedChange={v => setRules({...rules, free_for_residents: v})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <div><p className="text-sm font-medium">Subsidized Rate (%)</p><p className="text-xs text-muted-foreground">Discount for community members</p></div>
                  <Input type="number" className="w-20" min={0} max={100} value={rules.subsidized_rate_pct || 0}
                    onChange={e => setRules({...rules, subsidized_rate_pct: parseInt(e.target.value) || 0})} />
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <div><p className="text-sm font-medium">Operating Hours</p></div>
                  <div className="flex items-center gap-1">
                    <Input type="time" className="w-28" value={rules.operating_hours_start || "06:00"}
                      onChange={e => setRules({...rules, operating_hours_start: e.target.value})} />
                    <span className="text-xs">to</span>
                    <Input type="time" className="w-28" value={rules.operating_hours_end || "22:00"}
                      onChange={e => setRules({...rules, operating_hours_end: e.target.value})} />
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div><p className="text-sm font-medium">Maintenance Day</p><p className="text-xs text-muted-foreground">Weekly day for facility maintenance</p></div>
                  <Select value={rules.maintenance_day || "none"} onValueChange={v => setRules({...rules, maintenance_day: v === "none" ? null : v})}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {["MONDAY","TUESDAY","WEDNESDAY","THURSDAY","FRIDAY","SATURDAY","SUNDAY"].map(d => (
                        <SelectItem key={d} value={d}>{d.charAt(0) + d.slice(1).toLowerCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={saveRules} className="w-full rounded-full" data-testid="save-lgu-rules-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Rules
                </Button>
              </CardContent>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
